/**
 * Type definitions for Live Location Activity Dashboard
 * 
 * These types extend and complement the base locationActivity types
 * for queue management, carousel control, and visual presentation.
 * 
 * @author BizClick WMS
 * @version 1.25 - Fase 3 Implementation
 */

import { LocationActivityEvent } from '../../../../types/locationActivity';
import { RackStructure } from '../rack-builder/types';

/**
 * Queue item representing a rack with pending activities
 */
export interface QueueItem {
  /** Warehouse identifier */
  warehouseId: string;
  
  /** Rack number (as string to preserve leading zeros) */
  rackNumber: string;
  
  /** Events for this rack (for merge logic) */
  events: LocationActivityEvent[];
  
  /** Timestamp when first event was added to queue */
  enqueuedAt: Date;
  
  /** Timestamp of most recent event */
  lastEventAt: Date;
  
  /** Priority score (for future enhancements) */
  priority?: number;
}

/**
 * Queue statistics for monitoring
 */
export interface QueueStats {
  /** Total items in queue */
  queueLength: number;
  
  /** Number of unique racks in queue */
  uniqueRacks: number;
  
  /** Total events across all queue items */
  totalEvents: number;
  
  /** Average events per rack */
  avgEventsPerRack: number;
  
  /** Oldest item timestamp */
  oldestItemAge?: number;
}

/**
 * Configuration for ActivityQueueManager
 */
export interface QueueConfig {
  /** Merge events from same rack into single queue item */
  mergeSameRack: boolean;
  
  /** Maximum queue size (number of racks) */
  maxQueueSize: number;
  
  /** Time to display each rack (milliseconds) */
  dwellTime: number;
  
  /** Enable queue statistics tracking */
  enableStats: boolean;
}

/**
 * Carousel state for RackCarouselController
 */
export interface CarouselState {
  /** Currently displayed rack number (as string to preserve leading zeros) */
  currentRack: string | null;
  
  /** Warehouse ID for current rack */
  currentWarehouseId: string | null;
  
  /** Is carousel currently auto-switching */
  isAutoSwitching: boolean;
  
  /** Is carousel paused by user */
  isPaused: boolean;
  
  /** Timestamp when current rack was displayed */
  displayedAt: Date | null;
  
  /** Remaining dwell time (milliseconds) */
  remainingDwellTime: number;
  
  /** Next rack in queue */
  nextRack: QueueItem | null;
}

/**
 * Configuration for RackCarouselController
 */
export interface CarouselConfig {
  /** Enable automatic rack switching */
  autoSwitch: boolean;
  
  /** Time to display each rack (milliseconds) */
  dwellTime: number;
  
  /** Enable smooth transitions */
  smoothTransitions: boolean;
  
  /** Transition duration (milliseconds) */
  transitionDuration: number;
  
  /** Maximum racks in LRU cache */
  maxCacheSize: number;
  
  /** Preload next rack in background */
  preloadNext: boolean;
}

/**
 * Cache entry for LRU cache
 */
export interface CacheEntry {
  /** Rack number (as string to preserve leading zeros) */
  rackNumber: string;
  
  /** Warehouse ID */
  warehouseId: string;
  
  /** Cached rack structure */
  structure: RackStructure;
  
  /** Timestamp when loaded */
  loadedAt: Date;
  
  /** Timestamp of last access */
  lastAccessedAt: Date;
  
  /** Access count for statistics */
  accessCount: number;
}

/**
 * Cache statistics
 */
export interface CacheStats {
  /** Current cache size */
  size: number;
  
  /** Maximum cache size */
  maxSize: number;
  
  /** Cache hit count */
  hits: number;
  
  /** Cache miss count */
  misses: number;
  
  /** Cache hit rate (percentage) */
  hitRate: number;
  
  /** Total evictions */
  evictions: number;
}

/**
 * Transition state for animations
 */
export interface TransitionState {
  /** Is transition in progress */
  isTransitioning: boolean;
  
  /** Transition phase: 'fade-out' | 'loading' | 'fade-in' */
  phase: 'fade-out' | 'loading' | 'fade-in' | 'idle';
  
  /** Previous rack (for fade-out) */
  previousRack: string | null;
  
  /** Progress percentage (0-100) */
  progress: number;
}

/**
 * Queue operation result
 */
export interface QueueOperationResult {
  /** Operation successful */
  success: boolean;
  
  /** Result message */
  message?: string;
  
  /** Affected queue item */
  item?: QueueItem;
  
  /** Updated queue statistics */
  stats?: QueueStats;
}

/**
 * Carousel operation result
 */
export interface CarouselOperationResult {
  /** Operation successful */
  success: boolean;
  
  /** Result message */
  message?: string;
  
  /** Updated carousel state */
  state?: CarouselState;
  
  /** Cache statistics */
  cacheStats?: CacheStats;
}

/**
 * Default configurations
 */
export const DEFAULT_QUEUE_CONFIG: QueueConfig = {
  mergeSameRack: true,
  maxQueueSize: 100,
  dwellTime: 10000, // 10 seconds
  enableStats: true
};

export const DEFAULT_CAROUSEL_CONFIG: CarouselConfig = {
  autoSwitch: true,
  dwellTime: 10000, // 10 seconds
  smoothTransitions: true,
  transitionDuration: 500, // 500ms
  maxCacheSize: 10,
  preloadNext: true
};
